// React Query hooks do módulo Agenda — separado de hooks/queries/index.ts
// de propósito, mesmo padrão de hooks/queries/platform.ts: módulo próprio,
// chaves de cache próprias, nunca reaproveita queryKeys do resto do app.
// Usa a MESMA sessão de empresa (gpsdrive-client.ts) que todo o resto do
// GPS Drive — não é uma sessão separada como platform.ts.
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  gpsDriveAgendaService,
  type AgendaSettings,
  type AgendaChecklistTemplateItem,
  type CreateAgendaAppointmentInput,
  type CreateAgendaClosureReasonInput,
  type CreateAgendaTagInput,
  type CreateAgendaTechnicianInput,
  type CreateAgendaTopicInput,
  type ReopenAgendaAppointmentInput,
  type UpdateAgendaAppointmentInput,
  type UpdateAgendaClosureReasonInput,
  type UpdateAgendaTagInput,
  type UpdateAgendaTechnicianInput,
  type UpdateAgendaTopicInput,
} from "@/services/gpsdrive-agenda.service";

const dashboardKey = ["agenda", "dashboard"] as const;
const appointmentsKey = (weekStartIso: string) => ["agenda", "appointments", weekStartIso] as const;
const appointmentsMonthKey = (monthStartIso: string) =>
  ["agenda", "appointments-month", monthStartIso] as const;
const appointmentsRangeKey = (fromIso: string, toIso: string) =>
  ["agenda", "appointments-range", fromIso, toIso] as const;
const topicDistributionKey = (fromIso: string, toIso: string) =>
  ["agenda", "topic-distribution", fromIso, toIso] as const;
const techniciansKey = ["agenda", "technicians"] as const;
const topicsKey = ["agenda", "topics"] as const;
const tagsKey = ["agenda", "tags"] as const;
const closureReasonsKey = ["agenda", "closure-reasons"] as const;

export function useAgendaDashboardSummary() {
  return useQuery({
    queryKey: dashboardKey,
    queryFn: () => gpsDriveAgendaService.getDashboardSummary(),
    retry: false,
  });
}

// Filtro de período do painel "Distribuição por tópico" (pedido
// 2026-09-13) — separado de useAgendaDashboardSummary pra trocar de
// período sem precisar recarregar os outros cards/gráficos do dashboard.
export function useAgendaTopicDistribution(fromIso: string, toIso: string) {
  return useQuery({
    queryKey: topicDistributionKey(fromIso, toIso),
    queryFn: () => gpsDriveAgendaService.getDistribuicaoPorTopico(fromIso, toIso),
    retry: false,
  });
}

// Pré-carga (abaixo) não repete a busca de um vizinho buscado há menos de
// 30 s. Ao abrir a semana, o que está no cache aparece na hora e o React
// Query confere de novo em segundo plano (mudanças de outros usuários).
const PREFETCH_STALE_MS = 30_000;

export function useAgendaAppointments(weekStartIso: string) {
  return useQuery({
    queryKey: appointmentsKey(weekStartIso),
    queryFn: () => gpsDriveAgendaService.listAppointmentsForWeek(weekStartIso),
    retry: false,
  });
}

// "yyyy-mm-dd" (data local) deslocado em dias/meses, mesmo formato das chaves.
function shiftIsoDate(iso: string, days: number, months = 0): string {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1 + months, d + days);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Pedido 2026-10-26: clicar na seta pra semana passada demorava pra mostrar
// os atendimentos (a busca só começava no clique). Pré-carrega as semanas e
// meses vizinhos do que está na tela, então a seta já encontra no cache.
export function usePrefetchAgendaNeighbors(weekStartIso: string, monthStartIso: string) {
  const qc = useQueryClient();
  useEffect(() => {
    const timer = setTimeout(() => {
      for (const delta of [-7, 7]) {
        const iso = shiftIsoDate(weekStartIso, delta);
        void qc.prefetchQuery({
          queryKey: appointmentsKey(iso),
          queryFn: () => gpsDriveAgendaService.listAppointmentsForWeek(iso),
          staleTime: PREFETCH_STALE_MS,
        });
      }
      for (const delta of [-1, 1]) {
        const iso = shiftIsoDate(monthStartIso, 0, delta);
        void qc.prefetchQuery({
          queryKey: appointmentsMonthKey(iso),
          queryFn: () => gpsDriveAgendaService.listAppointmentsForMonth(iso),
          staleTime: PREFETCH_STALE_MS,
        });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [qc, weekStartIso, monthStartIso]);
}

// Range arbitrário (tela Chamados — hoje/ontem/este mês/mês anterior/
// personalizado, ver agenda-chamados.tsx) — mesmo endpoint de
// listAppointments já usado por dashboard/distribuição por tópico, só que
// agora com hook + cache próprios pra não recarregar toda vez que o filtro
// de status/técnico muda (esses filtram em memória sobre o mesmo range).
export function useAgendaAppointmentsRange(fromIso: string, toIso: string) {
  return useQuery({
    queryKey: appointmentsRangeKey(fromIso, toIso),
    queryFn: () => gpsDriveAgendaService.listAppointments(fromIso, toIso),
    retry: false,
  });
}

export function useAgendaAppointmentsMonth(monthStartIso: string) {
  return useQuery({
    queryKey: appointmentsMonthKey(monthStartIso),
    queryFn: () => gpsDriveAgendaService.listAppointmentsForMonth(monthStartIso),
    retry: false,
  });
}

// Busca de cliente do formulário (servidor filtra e limita a 30 + pré-
// cadastros) — `q` já chega com debounce do formulário.
export function useAgendaClientOptions(q: string, enabled: boolean) {
  return useQuery({
    queryKey: ["agenda", "client-options", q],
    queryFn: () => gpsDriveAgendaService.searchClientOptions(q),
    enabled,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
    retry: false,
  });
}

export function useCreateAgendaAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAgendaAppointmentInput) =>
      gpsDriveAgendaService.createAppointment(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agenda"] });
    },
  });
}

export function useUpdateAgendaAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateAgendaAppointmentInput }) =>
      gpsDriveAgendaService.updateAppointment(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agenda"] });
    },
  });
}

export function useDeleteAgendaAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => gpsDriveAgendaService.deleteAppointment(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agenda"] });
    },
  });
}

// Reabertura (tela Agenda > Atendimentos, ver agenda-atendimentos.tsx)
export function useReopenAgendaAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ReopenAgendaAppointmentInput }) =>
      gpsDriveAgendaService.reopenAppointment(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agenda"] });
    },
  });
}

export function useReleaseAgendaDeviceTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, password, note }: { id: string; password: string; note: string }) =>
      gpsDriveAgendaService.releaseDeviceTest(id, password, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agenda"] });
    },
  });
}

// Teste de equipamento pelo técnico (2026-10-08) ------------------------

const settingsKey = ["agenda", "settings"] as const;

export function useAgendaSettings() {
  return useQuery({ queryKey: settingsKey, queryFn: () => gpsDriveAgendaService.getSettings(), retry: false });
}

export function useUpdateAgendaSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AgendaSettings) => gpsDriveAgendaService.updateSettings(input),
    onSuccess: (data) => qc.setQueryData(settingsKey, data),
  });
}

export function useUpdateExternalTestPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (password: string) => gpsDriveAgendaService.updateExternalTestPassword(password),
    onSuccess: (data) => qc.setQueryData(settingsKey, data),
  });
}

export function useUpdateAgendaChecklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: AgendaChecklistTemplateItem[]) => gpsDriveAgendaService.updateChecklist(items),
    onSuccess: (data) => qc.setQueryData(settingsKey, data),
  });
}

// Técnicos ------------------------------------------------------------

export function useAgendaTechnicians() {
  return useQuery({
    queryKey: techniciansKey,
    queryFn: () => gpsDriveAgendaService.listTechnicians(),
    retry: false,
  });
}

export function useCreateAgendaTechnician() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAgendaTechnicianInput) => gpsDriveAgendaService.createTechnician(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: techniciansKey }),
  });
}

export function useUpdateAgendaTechnician() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateAgendaTechnicianInput }) =>
      gpsDriveAgendaService.updateTechnician(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: techniciansKey }),
  });
}

// Tópicos ------------------------------------------------------------

export function useAgendaTopics() {
  return useQuery({
    queryKey: topicsKey,
    queryFn: () => gpsDriveAgendaService.listTopics(),
    retry: false,
  });
}

export function useCreateAgendaTopic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAgendaTopicInput) => gpsDriveAgendaService.createTopic(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: topicsKey }),
  });
}

export function useUpdateAgendaTopic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateAgendaTopicInput }) =>
      gpsDriveAgendaService.updateTopic(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: topicsKey }),
  });
}

// Tags ------------------------------------------------------------

export function useAgendaTags() {
  return useQuery({
    queryKey: tagsKey,
    queryFn: () => gpsDriveAgendaService.listTags(),
    retry: false,
  });
}

export function useCreateAgendaTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAgendaTagInput) => gpsDriveAgendaService.createTag(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: tagsKey }),
  });
}

export function useUpdateAgendaTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateAgendaTagInput }) =>
      gpsDriveAgendaService.updateTag(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: tagsKey }),
  });
}

// Causas de encerramento ------------------------------------------------------

export function useAgendaClosureReasons() {
  return useQuery({
    queryKey: closureReasonsKey,
    queryFn: () => gpsDriveAgendaService.listClosureReasons(),
    retry: false,
  });
}

export function useCreateAgendaClosureReason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAgendaClosureReasonInput) => gpsDriveAgendaService.createClosureReason(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: closureReasonsKey }),
  });
}

export function useUpdateAgendaClosureReason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateAgendaClosureReasonInput }) =>
      gpsDriveAgendaService.updateClosureReason(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: closureReasonsKey }),
  });
}

// Cadastrar/Editar dispositivo → "Puxar dados da Agenda" (2026-10-14).
export function useAgendaManualVehicles(q: string, enabled: boolean) {
  return useQuery({
    queryKey: ["agenda", "manual-vehicles", q],
    queryFn: () => gpsDriveAgendaService.searchManualVehicles(q),
    enabled,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    retry: false,
  });
}
